import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'

/**
 * Metadatos de analítica de un enlace del footer (`Footer.csv`). Se serializan
 * como `data-fc-*` para que `FooterClickTracker` emita un único `click` por
 * pulsación (delegación) sin convertir en cliente cada hoja del footer.
 */
export interface FooterClickMeta {
  readonly clickType: string
  readonly clickSubtype: string
  /** Texto del enlace; se slugifica al emitir. Ausente → `no_aplica`. */
  readonly linkText?: string
  readonly moduleType?: string
  readonly actionDetail?: string
  readonly actionContext?: string
  /** `WhatsApp`: `link_url` y `link_domain` son `no_aplica` (`Footer.csv`). */
  readonly omitLink?: boolean
}

export const FOOTER_CLICK_MODULE_NAME = 'footer'
export const FOOTER_CLICK_ATTR = 'data-fc-type'

export function footerClickAttrs(meta: FooterClickMeta): Record<string, string> {
  const attrs: Record<string, string> = {
    [FOOTER_CLICK_ATTR]: meta.clickType,
    'data-fc-subtype': meta.clickSubtype,
  }
  if (meta.linkText !== undefined) attrs['data-fc-text'] = meta.linkText
  if (meta.moduleType) attrs['data-fc-module-type'] = meta.moduleType
  if (meta.actionDetail) attrs['data-fc-detail'] = meta.actionDetail
  if (meta.actionContext) attrs['data-fc-context'] = meta.actionContext
  if (meta.omitLink) attrs['data-fc-omit-link'] = 'true'
  return attrs
}

const OTHER_WEBS_GROUP_ID = 'other-webs-group'

/** Criterio de columna: "Otras webs" es outbound; el resto, `footer` + título del grupo. */
export function groupClickMeta(group: { id: string; title: string }): FooterClickMeta {
  if (group.id === OTHER_WEBS_GROUP_ID) {
    return { clickType: 'outbound', clickSubtype: 'otras_webs', moduleType: FOOTER_CLICK_MODULE_NAME }
  }
  return { clickType: 'footer', clickSubtype: slugify(group.title.replace(/:$/, '')) }
}

export const LEGAL_LINKS_CLICK_META: FooterClickMeta = {
  clickType: 'footer',
  clickSubtype: 'enlaces_legales',
}

export const EVENT_LINKS_CLICK_META: FooterClickMeta = {
  clickType: 'footer',
  clickSubtype: 'entradas_para_eventos',
}

export const CONTACT_SCHEDULE_CLICK_META: FooterClickMeta = {
  clickType: 'footer',
  clickSubtype: 'contacto',
}

/** WhatsApp Postventa (`Footer.csv` → "Click en teléfono WhatsApp Postventa"). */
export function whatsappClickMeta(phone: string): FooterClickMeta {
  return {
    clickType: 'whatsapp',
    clickSubtype: NO_APLICA,
    linkText: phone,
    actionDetail: 'footer_postventa',
    omitLink: true,
  }
}

export function trustSealClickMeta(): FooterClickMeta {
  return {
    clickType: 'outbound',
    clickSubtype: 'sello_confianza',
    actionDetail: 'confianza_online',
  }
}

export function socialClickMeta(label: string): FooterClickMeta {
  return {
    clickType: 'outbound',
    clickSubtype: 'social_follow',
    actionDetail: slugify(label),
  }
}

const STORE_LINK_TEXT: Record<string, string> = { apple: 'apple_store', play: 'google_play' }

export function storeClickMeta(icon: string): FooterClickMeta {
  return {
    clickType: 'outbound',
    clickSubtype: 'app_download',
    linkText: STORE_LINK_TEXT[icon] ?? icon,
  }
}
